import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { clipboardCopy } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import useParamId from '../../hooks/useParamId'
import { useNavigate } from '../../hooks/useNavigate'

import { getNoticeDetail } from '../../api/notice'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap, RowBetween } from '../../components/common'
import { Spinner } from '../../components/spinner/Spinner'
import { Header } from '../../components/header'
import { ArtipioContentsHeader, ArtipioContents, ArtipioContentItem } from '../../components/contents'

import { ReactComponent as HomeIcon } from '../../assets/ico/detail/home_black.svg'
import { ReactComponent as ShareIcon } from '../../assets/ico/detail/share_black.svg'

export function NoticeDetail() {
  const {
    commonStore: { setShowTab },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const noticeId = useParamId()

  const {
    data: noticeDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.notice, { noticeId }],
    queryFn: async () => await getNoticeDetail(noticeId as number),
    enabled: typeof noticeId === 'number',
  })

  const HEAD_INFO = {
    title: `${noticeDetail?.title} | 공지사항 | 미술품 조각투자 플래폼, 아티피오`,
    ogTitle: `${noticeDetail?.title} | 공지사항 | 미술품 조각투자 플래폼, 아티피오`,
    ogDescription: '',
    ogUrl: window.location.href,
  }

  useEffect(() => {
    setShowTab(false)
  }, [])

  if (isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const goHome = () => navigate('/home')

  const copyUrl = () => clipboardCopy(window.location.href, { message: '공지 페이지 주소가 복사되었어요.' })

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header
          type="SUB"
          rightItem={
            <HeaderRightItem $gap={16}>
              <ShareIcon onClick={copyUrl} />
              <HomeIcon onClick={goHome} />
            </HeaderRightItem>
          }
        />
        <StyledContent
          isLastImg={noticeDetail.contents?.[(noticeDetail.contents?.length as number) - 1]?.type === 'image'}
        >
          <ArtipioContentsHeader>
            <ArtipioContentsHeader.Title>{noticeDetail.title}</ArtipioContentsHeader.Title>
            <ArtipioContentsHeader.Date>
              {dayjs(noticeDetail.createdDate ?? '0000-00-00T00:00:00.000Z').format('YYYY.MM.DD')}
            </ArtipioContentsHeader.Date>
          </ArtipioContentsHeader>
          <ArtipioContents>
            {noticeDetail.contents?.map(({ type, content, prop }, i) => {
              const isFirstImg = type === 'image' && i === 0
              const isLastImg = type === 'image' && i === Number(noticeDetail.contents?.length) - 1
              return (
                <ArtipioContentItem
                  key={i}
                  type={type}
                  content={content}
                  prop={prop}
                  alt={`img-${i}`}
                  isFirstImg={isFirstImg}
                  isLastImg={isLastImg}
                />
              )
            })}
          </ArtipioContents>
        </StyledContent>
      </Wrap>
    </>
  )
}

const HeaderRightItem = styled(RowBetween)`
  > * {
    cursor: pointer;
  }
`

const StyledContent = styled(Content)<{ isLastImg: boolean }>`
  ${({ isLastImg }) => (isLastImg ? '' : 'padding-bottom: 108px;')}
`
